import { useAuth } from "react-oidc-context";
import { Button } from "../f7";
import type { AppConfig } from "../config";

/**
 * Start sign-in.
 *
 * With Google configured, `identity_provider` skips Cognito's own provider
 * picker so the button lands on Google's account chooser rather than an
 * interstitial offering exactly one choice.
 *
 * WITHOUT it, that parameter must not be sent at all: Cognito refuses a
 * provider it does not have with "Login option is not available. Please try
 * another one", and the user cannot sign in by any route. The pool ships with
 * no Google IdP until the `cognito.google` config block is filled in, so this
 * is the state dev and prod are actually in today.
 */
export function startSignIn(
  auth: ReturnType<typeof useAuth>,
  googleEnabled: boolean | undefined,
): void {
  if (googleEnabled) {
    void auth.signinRedirect({ extraQueryParams: { identity_provider: "Google" } });
    return;
  }
  void auth.signinRedirect();
}

/**
 * Cognito's PreSignUp trigger refuses a Google sign-in from someone nobody
 * provisioned, and its message arrives here in the error description.
 *
 * Matched on the trigger's own wording (lambda/presignup_guard/index.py). A
 * miss is not silent: the generic error panel still renders, it just reads as
 * a fault rather than as "ask someone to provision you".
 */
export function isNotProvisioned(error: Error | undefined): boolean {
  if (!error) return false;
  return /provisioning gap|has no Foodics AI Admin account|PreSignUp failed/i.test(
    error.message,
  );
}

/**
 * Cognito fails the VERY sign-in during which a PreSignUp trigger links a
 * federated identity — "already found an entry for username" — and succeeds on
 * every one after. Every new joiner hits this exactly once.
 *
 * So retry once, silently. The sessionStorage flag is what stops a genuinely
 * broken pool from looping forever: it survives the redirect, and a successful
 * sign-in never reads it again.
 */
const RELINK_RETRY_KEY = "orbit_sso_relink_retry";

export function isFirstLinkFailure(error: Error | undefined): boolean {
  if (!error) return false;
  return /already found an entry for username|AliasExistsException/i.test(
    error.message,
  );
}

export function shouldRetryRelink(error: Error | undefined): boolean {
  if (!isFirstLinkFailure(error)) return false;
  try {
    if (sessionStorage.getItem(RELINK_RETRY_KEY)) return false;
    sessionStorage.setItem(RELINK_RETRY_KEY, "1");
    return true;
  } catch {
    // Private mode, or storage disabled. Retrying without a guard risks a
    // loop, so take the visible error over the invisible one.
    return false;
  }
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-md border border-slate-950/10 dark:border-white/10 bg-white dark:bg-slate-900 p-6 text-center shadow-lg">
        {children}
      </div>
    </div>
  );
}

export function NotProvisioned({ email }: { email?: string }) {
  return (
    <Card>
      <h1 className="text-lg font-semibold">No account on this console</h1>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        {email ? <><span className="font-medium">{email}</span> signed in</> : "You signed in"} with
        Google, but there is no Foodics AI Admin account for that address.
      </p>
      <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
        Access is provisioned per person. Ask the AI Engineering team to add
        you — this is a provisioning gap, not a password problem.
      </p>
    </Card>
  );
}

export function SignIn({ config }: { config: AppConfig }) {
  const auth = useAuth();
  const google = config.googleEnabled === true;

  if (isNotProvisioned(auth.error)) return <NotProvisioned />;

  return (
    <Card>
      <h1 className="text-lg font-semibold">Foodics AI Admin</h1>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {google
          ? "Internal console. Sign in with your Foodics Google account."
          : "Internal console. Sign in with your Foodics account."}
      </p>
      <Button
        onClick={() => startSignIn(auth, config.googleEnabled)}
        className="mt-5 w-full"
      >
        {google ? "Continue with Google" : "Sign in"}
      </Button>
      {auth.error && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">{auth.error.message}</p>
      )}
    </Card>
  );
}
